import { useState, useCallback } from 'react';
import { ContractFormData } from '../types';
import { UtilityType } from '../../../../types/utility.types';

interface UseContractFormReturn {
  formData: ContractFormData;
  setFormData: React.Dispatch<React.SetStateAction<ContractFormData>>;
  handleFormChange: (field: string, value: any) => void;
  handleNestedChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  resetForm: () => void;
}

const getInitialFormData = (
  clientIdFromQuery?: string,
  propertyIdFromQuery?: string
): ContractFormData => ({
  serviceType: '' as UtilityType,
  accountNumber: '',
  status: '',
  notes: '',
  supplier: '',
  meterDetails: {
    meterType: '',
    location: '',
    serialNumber: '',
    mpanMprnSpid: '',
  },
  subServices: [],
  contractStartDate: null,
  contractEndDate: null,
  currentRate: '',
  eacAq: '',
  clientId: clientIdFromQuery || undefined,
  propertyId: propertyIdFromQuery || undefined,
  standingCharge: undefined,
  commissionUplift: '',
  contractValue: '',
  annualUsage: '',
  dayRate: undefined,
  dayConsumption: undefined,
  nightRate: undefined,
  nightConsumption: undefined,
  weekendRate: undefined,
  weekendConsumption: undefined,
  uplift: undefined,
});

export const useContractForm = (
  clientIdFromQuery?: string,
  propertyIdFromQuery?: string
): UseContractFormReturn => {
  const [formData, setFormData] = useState<ContractFormData>(
    getInitialFormData(clientIdFromQuery, propertyIdFromQuery)
  );

  // Handle form field changes
  const handleFormChange = useCallback((field: string, value: any) => {
    console.log(`Updating field: ${field} with value:`, value);
    
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [field]: value,
      };
      
      // Special handling for serviceType changes
      if (field === 'serviceType') {
        console.log('Service type changed to:', value);
        // Reset service-specific fields when type changes
        newFormData.meterDetails = {
          meterType: '',
          location: '',
          serialNumber: '',
          mpanMprnSpid: '',
        };
        newFormData.eacAq = '';
        newFormData.standingCharge = undefined;
        newFormData.contractValue = '';
        newFormData.subServices = [];
        
        // Reset the legacy energy rate fields
        newFormData.dayUnitRate = '';
        newFormData.nightUnitRate = '';
        newFormData.eveningWeekendRate = '';
        newFormData.eveningWeekendConsumption = '';
        
        // Reset the new numeric energy rate fields
        newFormData.dayRate = undefined;
        newFormData.dayConsumption = undefined;
        newFormData.nightRate = undefined;
        newFormData.nightConsumption = undefined;
        newFormData.weekendRate = undefined;
        newFormData.weekendConsumption = undefined;
        newFormData.uplift = undefined;
      }
      
      return newFormData;
    });
  }, []);

  // Handle nested field changes (like meterDetails)
  const handleNestedChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData((prev) => {
      if (name.includes('.')) {
        const [parent, child] = name.split('.');
        const parentObj = prev[parent as keyof ContractFormData];
        
        if (parentObj && typeof parentObj === 'object' && !Array.isArray(parentObj)) {
          return {
            ...prev,
            [parent]: {
              ...parentObj,
              [child]: value,
            },
          };
        }
      }
      
      return {
        ...prev,
        [name]: value,
      };
    });
  }, []);

  const resetForm = useCallback(() => {
    setFormData(getInitialFormData(clientIdFromQuery, propertyIdFromQuery));
  }, [clientIdFromQuery, propertyIdFromQuery]);

  return {
    formData,
    setFormData,
    handleFormChange,
    handleNestedChange,
    resetForm,
  };
};
